import { Box, Button, Heading, HStack, Link, Stack, Table, Tbody, Td, Text, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  SavedReportDto,
  SavedReportKindDto,
  useCreateSavedReportMutation,
  useDeleteSavedReportMutation,
  useListSavedReportsQuery,
  useRenameSavedReportMutation,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { HorizontalScrollBox } from "../../common/HorizontalScrollBox";
import { isNotFoundError } from "../../common/errors/isNotFoundError";
import { useApiError } from "../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../common/formatting/useDisplayDateWithTime";
import { ListLoadError } from "../../common/ListLoadError";
import { ReportNameModal } from "../../common/saved-reports/ReportNameModal";
import {
  canOpenSavedReportList,
  savedReportLink,
  savedReportListLabel,
} from "../../common/saved-reports/savedReportLists";
import { savedReportQuery } from "../../common/saved-reports/expiringThisYearReport";
import { personName } from "../../common/components/communication/communicationUtils";
import { useNavAccess } from "../../common/layout/useNavAccess";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import { useSkeletons } from "../../common/useSkeletons";
import { ShareReportModal } from "./ShareReportModal";

export const MyReports = () => {
  const { t, i18n } = useTranslation();
  const { access } = useNavAccess();
  const displayDateWithTime = useDisplayDateWithTime();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { data, isError, isFetching, refetch } = useListSavedReportsQuery({ organizationId: selectedOrg.id });
  const [renaming, setRenaming] = useState<SavedReportDto>();
  const [copying, setCopying] = useState<SavedReportDto>();
  const [sharingId, setSharingId] = useState<string>();
  const sharing = data?.reports.find((report) => report.id === sharingId);
  const [renameSavedReport, { isLoading: isRenaming }] = useRenameSavedReportMutation();
  const [createSavedReport, { isLoading: isCopying }] = useCreateSavedReportMutation();
  const [deleteSavedReport] = useDeleteSavedReportMutation();
  const toast = useToast();
  const { trigger } = useApiError();
  const confirmation = useConfirmationModal();
  const skeletons = useSkeletons().stackedLines;
  const firmReports = data?.reports.filter((report) => report.kind === SavedReportKindDto.Firm) ?? [];
  const otherReports = data?.reports.filter((report) => report.kind !== SavedReportKindDto.Firm) ?? [];

  const rename = async (report: SavedReportDto, name: string) => {
    try {
      await renameSavedReport({ savedReportId: report.id, renameSavedReportRequest: { name } }).unwrap();
      setRenaming(undefined);
    } catch (e) {
      if (isNotFoundError(e)) setRenaming(undefined);
      trigger(e);
    }
  };

  const saveCopy = async (report: SavedReportDto, name: string) => {
    try {
      await createSavedReport({
        createSavedReportRequest: { name, list: report.list, query: savedReportQuery(report) },
      }).unwrap();
      toast({ title: t("The report is saved in My reports"), status: "success" });
      setCopying(undefined);
    } catch (e) {
      trigger(e);
    }
  };

  const askToDelete = (report: SavedReportDto) =>
    confirmation.show({
      title: String(t("Delete report")),
      description: String(
        report.kind === SavedReportKindDto.Firm
          ? t(
              "«{{name}}» is a firm report. It disappears for everyone in the organisation. The list it opens is not changed.",
              { name: report.name }
            )
          : report.sharedWith.length > 0
          ? t(
              "«{{name}}» is shared with {{colleagues}}. It disappears from their My reports too. The list it opens is not changed.",
              {
                name: report.name,
                colleagues: new Intl.ListFormat(i18n.language, { type: "conjunction" }).format(
                  report.sharedWith.map(personName)
                ),
              }
            )
          : t("«{{name}}» is deleted from My reports. The list it opens is not changed.", { name: report.name })
      ),
      confirmButtonText: String(t("Delete")),
      confirmButtonColor: "red",
      size: "md",
      onConfirmed: async () => {
        try {
          await deleteSavedReport({ savedReportId: report.id }).unwrap();
        } catch (e) {
          if (!isNotFoundError(e)) trigger(e);
        }
      },
    });

  const reportsTable = (reports: SavedReportDto[]) => (
    <HorizontalScrollBox borderRadius="lg" border="1px solid" borderColor="smBorder" width="full" maxWidth="full">
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>{t("Name")}</Th>
            <Th>{t("List")}</Th>
            <Th>{t("Last changed")}</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {reports.map((report) => {
            const opens = report.kind === SavedReportKindDto.Own || canOpenSavedReportList(access, report.list);
            return (
            <Tr key={report.id}>
              <Td>
                <Stack spacing="0">
                  {opens ? (
                    <Link as={RouterLink} to={savedReportLink(report)} color="smPrimary" fontWeight="semibold">
                      {report.name}
                    </Link>
                  ) : (
                    <Text fontWeight="semibold">{report.name}</Text>
                  )}
                  {!opens && (
                    <Text fontSize="xs" color="smMuted">
                      {t("You don't have access to {{list}}", { list: savedReportListLabel(t, report.list) })}
                    </Text>
                  )}
                  {report.sharedBy ? (
                    <Text fontSize="xs" color="smMuted">
                      {t("Shared by {{name}}", { name: personName(report.sharedBy) })}
                    </Text>
                  ) : (
                    report.sharedWith.length > 0 && (
                      <Text fontSize="xs" color="smMuted">
                        {t("Shared with {{count}} colleagues", { count: report.sharedWith.length })}
                      </Text>
                    )
                  )}
                </Stack>
              </Td>
              <Td>{savedReportListLabel(t, report.list)}</Td>
              <Td>{displayDateWithTime(report.modifiedAt)}</Td>
              <Td>
                <HStack justify="flex-end" spacing="1">
                  {!report.canChange ? (
                    opens && (
                      <Button size="xs" variant="ghost" onClick={() => setCopying(report)}>
                        {t("Save as my own report")}
                      </Button>
                    )
                  ) : (
                    <>
                      {report.canShare && (
                        <Button size="xs" variant="ghost" onClick={() => setSharingId(report.id)}>
                          {t("Share")}
                        </Button>
                      )}
                      <Button size="xs" variant="ghost" onClick={() => setRenaming(report)}>
                        {t("Rename")}
                      </Button>
                      <Button size="xs" variant="ghost" colorScheme="red" onClick={() => askToDelete(report)}>
                        {t("Delete")}
                      </Button>
                    </>
                  )}
                </HStack>
              </Td>
            </Tr>
            );
          })}
        </Tbody>
      </Table>
    </HorizontalScrollBox>
  );

  return (
    <Box>
      <Heading as="h1" size="lg" color="smText" mb="2">
        {t("My reports")}
      </Heading>
      <Text color="smMuted" mb="6">
        {t(
          "Lists you saved with «Save as report», lists colleagues shared with you, and the organisation's firm reports. Each opens with today's data and shows only what you have access to."
        )}
      </Text>
      {isError ? (
        <ListLoadError
          message={t("Could not load your reports.")}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
        />
      ) : !data ? (
        skeletons(0, 3)
      ) : data.reports.length === 0 ? (
        <Text color="smMuted" py="10" textAlign="center">
          {t(
            "You have no saved reports yet. Use «Save as report» on the contracts, the procurements or the contract tasks."
          )}
        </Text>
      ) : (
        <Stack spacing="6">
          {firmReports.length > 0 && (
            <Box>
              <Heading as="h2" size="sm" color="smText" mb="2">
                {t("Firm reports")}
              </Heading>
              {reportsTable(firmReports)}
            </Box>
          )}
          {otherReports.length > 0 && (
            <Box>
              {firmReports.length > 0 && (
                <Heading as="h2" size="sm" color="smText" mb="2">
                  {t("My reports and reports shared with me")}
                </Heading>
              )}
              {reportsTable(otherReports)}
            </Box>
          )}
        </Stack>
      )}
      {renaming && (
        <ReportNameModal
          title={String(t("Rename report"))}
          confirmLabel={String(t("Save"))}
          initialName={renaming.name}
          isLoading={isRenaming}
          onConfirm={(name) => void rename(renaming, name)}
          onClose={() => setRenaming(undefined)}
        />
      )}
      {copying && (
        <ReportNameModal
          title={String(t("Save as my own report"))}
          description={String(
            t(
              "Your copy keeps the filter, the columns and the sorting. Changes the owner makes later do not reach it, and only you see it."
            )
          )}
          confirmLabel={String(t("Save"))}
          initialName={copying.name}
          isLoading={isCopying}
          onConfirm={(name) => void saveCopy(copying, name)}
          onClose={() => setCopying(undefined)}
        />
      )}
      {sharing && <ShareReportModal report={sharing} onClose={() => setSharingId(undefined)} />}
      {confirmation.modal}
    </Box>
  );
};
